import { displayTime12 } from '../../group_leader/helpers/time'
import { playerFullName } from '../../group_leader/helpers/player'
import {
  fieldCourseId, holeTypeFromTee, HOLES_PLAYED_KEYS, runInterval, startHoleNumber, to24hr,
} from './pairings'
import { specDisplayName } from './games'

export { displayTime12, specDisplayName }

// Pure summary logic behind the Review step: game naming, roster, course and schedule.

// The "18 holes · Net · Stroke Play" line under a game's name.
export const specDetailLine = (spec) => {
  const selections = spec.node_selections || {}
  return [ 'holes', 'handicap', 'format' ]
    .map((key) => selections[key]?.name)
    .filter(Boolean)
    .join(' · ')
}

const GROUP_SIZE_KEYS = {
  2: 'group_size_twosomes',
  3: 'group_size_threesomes',
  4: 'group_size_foursomes',
}

// Named sizes only; anything larger falls back to the generic "Groups of n".
export const groupSizeKey = (size) => GROUP_SIZE_KEYS[parseInt(size, 10)] || null

export const summarizeRoster = (foursomes, unplacedPlayers) => {
  // A blind draw occupies a seat server-side, so a group holding only blinds is still a group.
  const occupiedGroups = foursomes.filter((group) => (group.attributes?.players || []).length
    + (group.attributes?.blinds || []).length > 0)
  const placedPlayers = occupiedGroups.flatMap((group) => group.attributes?.players || [])

  return {
    occupiedGroups,
    placedPlayers,
    groupCount: occupiedGroups.length,
    totalEnrolled: placedPlayers.length + unplacedPlayers.length,
  }
}

// Seat order, with a blind draw bracketed the way the Pairings sheet writes it.
export const groupOccupantNames = (group) => {
  const attributes = group.attributes || {}
  return [
    ...(attributes.players || []).map((player) => [ player.position ?? 0, playerFullName(player) ]),
    ...(attributes.blinds || []).map((blind) => [ blind.position ?? 0, `[${playerFullName(blind)}]` ]),
  ]
    .filter(([ , name ]) => name && name !== '[]')
    .sort(([ left ], [ right ]) => left - right)
    .map(([ , name ]) => name)
}

// RTK keeps the last good data on a failed refetch, so only an empty-handed read blanks.
export const lostItsData = (queries) => queries.some(({ error, data }) => !!error && !data)

// Returns the Course & Tee step's own key, so the two screens cannot name the holes differently.
export const holesPlayedKey = (holeType) => HOLES_PLAYED_KEYS[holeType] || null

// One entry per distinct tee in play, with how many golfers are on it.
export const teeCounts = (players) => {
  const byTee = {}

  players.forEach((player) => {
    if (!player.tee?.id) { return }
    const key = String(player.tee.id)
    if (!byTee[key]) { byTee[key] = { name: player.tee.name || '-', count: 0 } }
    byTee[key].count += 1
  })

  return Object.values(byTee)
}

// From the sheet as well as the config; `known` separates "no course" from "cannot see it".
export const resolveCourseAndTee = ({ setupConfig, courses, placedPlayers, tees }) => {
  // Counted, not first-found: a golfer can inherit a tee from another course (see pairings.js).
  const courseId = fieldCourseId(placedPlayers, setupConfig?.courseId)
  const course = courses.find((entry) => String(entry.id) === String(courseId))
  const courseName = setupConfig?.courseName || course?.attributes?.name || null

  // Walk-on keeps its choice on the round; otherwise a golfer's own tee says which holes.
  const fieldTee = placedPlayers
    .find((player) => player.tee?.id && String(player.tee.course_id || '') === String(courseId))?.tee

  return {
    courseName,
    teeName: setupConfig?.teeName || (tees.length === 1 ? tees[0].name : null),
    known: !!courseName,
    holeType: setupConfig?.walkonHoleType || holeTypeFromTee(fieldTee),
  }
}

// Every timed group sharing one time is a shotgun, the test PairingsStep applies too.
const sheetIsShotgun = (timedGroups) => timedGroups.length > 1
  && timedGroups.every((group) => group.attributes.tee_time === timedGroups[0].attributes.tee_time)

// The run's interval by the rule the Pairings panel applies, so the two cannot disagree.
const sheetInterval = (timedGroups) =>
  runInterval(timedGroups.map((group) => to24hr(group.attributes.tee_time)).filter(Boolean))

// The sheet is durable and the config only the leader's last click, so the sheet answers.
export const resolveSchedule = (setupConfig, foursomes) => {
  const timedGroups = foursomes.filter((group) => group.attributes?.tee_time)
  const isShotgun = timedGroups.length > 1
    ? sheetIsShotgun(timedGroups)
    : setupConfig?.startType === 'shotgun'

  const configTime = isShotgun ? setupConfig?.shotgunTime : setupConfig?.teeTime
  const time = displayTime12(timedGroups[0]?.attributes?.tee_time) || displayTime12(configTime)
  // `||`, not `??`: the hole field may be '' mid-typing, which would resolve to hole 1.
  const startHole = timedGroups[0]?.attributes?.hole
    || timedGroups[0]?.attributes?.tee_number
    || setupConfig?.startHole
  // Three rows is what runInterval needs to answer; below that only the config can.
  const interval = timedGroups.length > 2
    ? sheetInterval(timedGroups)
    : (setupConfig?.interval || null)

  return {
    isShotgun,
    time,
    interval: isShotgun ? null : interval,
    // A number, so the caller no longer has to know about the legacy 'hole1' / 'hole10' values.
    startHole: startHole ? startHoleNumber(startHole) : null,
    isSet: !!(time || setupConfig?.startType),
  }
}
